import React, { useState, useEffect, useRef } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from "react-i18next";
import axiosInstance from '@/api/axios';
import { InfoTooltip } from './Infotooltip';
import { CardExportMenu } from './export/CardExportMenu';
import { describePeriod } from './export/exportUtils';

function getColorScheme(amount) {
  if (amount >= 1000) return { primary: '#10b981', text: 'text-green-600 dark:text-green-400' };
  if (amount >= 500)  return { primary: '#3b82f6', text: 'text-blue-500 dark:text-blue-400' };
  return                     { primary: '#f59e0b', text: 'text-yellow-600 dark:text-yellow-400' };
}

// Placeholder bars (all zero) so the card keeps its chart area when no bins
// are selected.
const EMPTY_BARS = Array.from({ length: 12 }, () => ({ date: '', diverted: 0, residual: 0, total: 0 }));

// Viewer's browser timezone: sent to the backend and used for the range label.
const TZ = (typeof Intl !== 'undefined' && Intl.DateTimeFormat().resolvedOptions().timeZone) || 'Europe/Amsterdam';

// "Oct 2 – Oct 3, 2026" (year once when both dates share it, otherwise on both).
const formatCustomRange = (startIso, endIso, locale) => {
  const start = new Date(startIso);
  const end   = new Date(endIso);
  const yearOf = (d) => d.toLocaleDateString('en', { year: 'numeric', timeZone: TZ });
  const sameYear = yearOf(start) === yearOf(end);
  const startLabel = start.toLocaleDateString(locale, {
    day: 'numeric', month: 'short', ...(sameYear ? {} : { year: 'numeric' }), timeZone: TZ,
  });
  const endLabel = end.toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric', timeZone: TZ });
  return `${startLabel} – ${endLabel}`;
};

export default function WasteDiverted({ selectedBins, selectedTimePeriod, year, customDateRange }) {
  const [animatedAmount, setAnimatedAmount] = useState(0);
  const animatedRef = useRef(0);
  const { t, i18n } = useTranslation();
  const cardRef = useRef(null);

  const isEmpty = !selectedBins || selectedBins.length === 0;

  // Stable key for query + useEffect
  const customDateRangeKey = (() => {
    const r = customDateRange?.[0];
    if (!r?.startDate || !r?.endDate) return null;
    const start = r.startDate instanceof Date ? r.startDate.toISOString() : r.startDate;
    const end   = r.endDate   instanceof Date ? r.endDate.toISOString()   : r.endDate;
    return `${start}__${end}`;
  })();

  const isCustom = Boolean(customDateRangeKey);

  const { data, isLoading, error } = useQuery({
    queryKey: ['wasteDiverted', selectedBins, selectedTimePeriod, year, customDateRangeKey],
    queryFn: async () => {
      const params = { 'bins[]': selectedBins, tz: TZ };

      if (isCustom) {
        const r = customDateRange[0];
        params.start_date = r.startDate instanceof Date ? r.startDate.toISOString() : r.startDate;
        params.end_date   = r.endDate   instanceof Date ? r.endDate.toISOString()   : r.endDate;
      } else {
        params.period = selectedTimePeriod;
        // Only the yearly view uses `year`. Sending it for 3/6 months made the
        // backend treat the request as "a specific year" (12 months).
        if (selectedTimePeriod === 'year') params.year = year;
      }

      const response = await axiosInstance.get('waste_diverted/', { params });
      return response.data;
    },
    enabled: !isEmpty,
  });

  useEffect(() => {
    // No bins → snap the counter back to 0 instead of leaving the old number.
    if (isEmpty) {
      animatedRef.current = 0;
      setAnimatedAmount(0);
      return;
    }
    if (data?.waste_diverted == null) return;

    const target = data.waste_diverted;
    const from = animatedRef.current;
    if (from === target) return;

    // Fixed-duration tween (ease-out): same ~0.8s whether the number goes
    // up or down, and no matter how far it has to travel.
    const DURATION = 800;
    const start = performance.now();
    let raf;

    const tick = (now) => {
      const p = Math.min((now - start) / DURATION, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      const value = p === 1 ? target : Math.round(from + (target - from) * eased);
      animatedRef.current = value;
      setAnimatedAmount(value);
      if (p < 1) raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [data?.waste_diverted, isEmpty]);

  // Date range badge — ONLY for a custom (calendar) selection. Every other
  // period returns null, so nothing is shown next to the info icon.
  // Formatted in the viewer's timezone, same as the buckets the backend returns.
  const getRangeLabel = () => {
    if (!isCustom || !data?.start_date || !data?.end_date) return null;
    return formatCustomRange(data.start_date, data.end_date, i18n.language);
  };

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0].payload;
    return (
      <div className="bg-white dark:bg-gray-800 p-2 border dark:border-gray-700 rounded-lg shadow text-xs">
        <p className="font-bold dark:text-gray-200 mb-1">{d.date}</p>
        <p className="text-green-600 dark:text-green-400">{t('wasteDiverted.tooltip.diverted')}: {d.diverted} kg</p>
        <p className="text-gray-500 dark:text-gray-400">{t('wasteDiverted.tooltip.residual')}: {d.residual} kg</p>
        <p className="text-gray-500 dark:text-gray-400">{t('wasteDiverted.tooltip.total')}: {d.total} kg</p>
      </div>
    );
  };

  if (!isEmpty && isLoading) return (
    // Skeleton — header stays generic (bins not confirmed yet), number
    // line + bar-chart silhouette fill the same slots the real chart uses.
    <div className="rounded-lg border bg-card text-card-foreground w-full h-[200px] flex flex-col motion-safe:animate-pulse" aria-busy="true">
      <span className="sr-only" role="status">{t('wasteDiverted.loading')}</span>
      <div className="px-4 py-3 flex items-center justify-between">
        <div className="h-4 w-28 rounded bg-gray-200 dark:bg-slate-700" />
        <div className="h-5 w-5 rounded-full bg-gray-200 dark:bg-slate-700" />
      </div>
      <div className="px-4 pb-1">
        <div className="h-6 w-20 rounded bg-gray-200 dark:bg-slate-700" />
      </div>
      <div className="flex-1 px-4 pb-4 flex items-end gap-1.5">
        {[40, 65, 50, 80, 55, 70, 45, 60, 75, 50, 65, 55].map((h, i) => (
          <div key={i} className="flex-1 rounded-t bg-gray-200 dark:bg-slate-700" style={{ height: `${h}%` }} />
        ))}
      </div>
    </div>
  );

  if (!isEmpty && error) return (
    <div className="rounded-lg border bg-card text-card-foreground w-full h-[200px] flex items-center justify-center">
      {t('wasteDiverted.error')}
    </div>
  );

  if (!isEmpty && !data) return (
    <div className="rounded-lg border bg-card text-card-foreground w-full h-[200px] flex items-center justify-center">
      {t('wasteDiverted.noData')}
    </div>
  );

  const chartData = isEmpty ? EMPTY_BARS : data.historical_data;
  const displayAmount = isEmpty ? 0 : animatedAmount;
  const rangeLabel = isEmpty ? null : getRangeLabel();

  // ── Export (card menu + page export both read this) ──
  const exportTitle = t('wasteDiverted.title');
  const exportMeta = {
    period: describePeriod({
      period: selectedTimePeriod, year, customDateRange, isCustomDateRange: isCustom,
    }),
    bins: selectedBins,
  };
  const getExportData = () => ({
    title: exportTitle,
    headers: ['Period', 'Diverted (kg)', 'Residual (kg)', 'Total (kg)'],
    rows: [
      ['Total diverted', Number(data?.waste_diverted ?? 0).toFixed(2), '', ''],
      ...(data?.historical_data || []).map(d => [
        d.date, Number(d.diverted ?? 0).toFixed(2), Number(d.residual ?? 0).toFixed(2), Number(d.total ?? 0).toFixed(2),
      ]),
    ],
  });

  return (
    <div
      ref={cardRef}
      className={`exportable-section rounded-lg border bg-card text-card-foreground flex flex-col w-full h-[200px] ${isEmpty ? '' : 'dark:hover:bg-slate-900 hover:cursor-pointer'}`}
      data-export-title={exportTitle}
      data-export-data={isEmpty ? undefined : JSON.stringify(getExportData())}
    >
      <div className="px-4 py-3 flex items-center justify-between">
        <h2 className="tracking-tight text-sm font-medium">{t('wasteDiverted.title')}</h2>
        <div className="flex items-center gap-2">
          {rangeLabel && (
            <span className="text-xs text-muted-foreground">{rangeLabel}</span>
          )}
          {!isEmpty && (
            <CardExportMenu compact cardRef={cardRef} title={exportTitle} meta={exportMeta} getData={getExportData} />
          )}
          <InfoTooltip text={t('wasteDiverted.infoTooltip', 'Total weight of waste diverted from residual by correct sorting into PMD, organic, or paper streams, excluding contaminated items.')} />
        </div>
      </div>

      <div className="px-4 pb-1 text-2xl font-bold text-green-600 dark:text-green-400">
        {displayAmount.toLocaleString()} kg
      </div>

      <div className="flex-1 w-full px-0 pt-0 overflow-hidden rounded-b-lg">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} margin={{ top: 5, right: 10, left: 10, bottom: 20 }}>
            <XAxis
              dataKey="date"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 10, fill: '#9CA3AF' }}
              dy={10}
              interval="preserveStartEnd"
            />
            <YAxis hide domain={isEmpty ? [0, 1] : [0, 'auto']} />
            {!isEmpty && <Tooltip content={<CustomTooltip />} wrapperStyle={{ outline: 'none' }} />}
            <Bar dataKey="diverted" radius={[4, 4, 0, 0]}>
              {chartData.map((_, index) => (
                <Cell key={`cell-${index}`} fill="#10b981" />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}